import type { Theme } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type {
	ClientPortfolioDomainEnum,
	PortfolioListItemResponseDTO,
	PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { getPortfolioRowValueKind } from "web_myplann/Portfolio/Helpers/getPortfolioRowValueKind";
import { PortfolioRowValueKind } from "web_myplann/Portfolio/Types/portfolioRowValueKind";
import { ListItemMoneyValue } from "web_myplann/Shared/Components/ListItemMoneyValue";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { roundNumber } from "web_myplann/Shared/Utils/numberFormatters";
import { normalizeChange } from "web_myplann/Shared/Utils/percentageFormatters";

function renderInsuranceRow(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	let dateRange = "";
	if (data.startAt) {
		const endDate = data.endAt
			? dateFormatter(data.endAt)
			: t(Resources.Common.NotSpecified);
		dateRange = `${dateFormatter(data.startAt)} - ${endDate}`;
	}

	return (
		<Typography
			variant="deprecatedBody1"
			color={theme.palette.deprecated.grey[500]}
		>
			{dateRange}
		</Typography>
	);
}

function renderValueWithDelta(
	currentValue: number | null,
	change: number | null,
	currencySign: string,
): ReactNode {
	return (
		<ListItemMoneyValue
			value={currentValue}
			currencySign={currencySign}
			adornment={
				currentValue != null && currentValue !== 0 ? (
					<PercentageDelta change={change} />
				) : undefined
			}
		/>
	);
}

function renderValueOnly(
	currentValue: number | null,
	currencySign: string,
): ReactNode {
	return (
		<ListItemMoneyValue value={currentValue} currencySign={currencySign} />
	);
}

function renderServiceRow(
	data: PortfolioListItemResponseDTO,
	theme: Theme,
): ReactNode {
	return (
		<Stack direction="row" gap={0.8}>
			<Typography
				variant="deprecatedBody1"
				color={theme.palette.deprecated.grey[500]}
				overflow="hidden"
				whiteSpace="nowrap"
				maxWidth="100%"
				textOverflow="ellipsis"
			>
				{data.contractNumber}
			</Typography>
		</Stack>
	);
}

export function renderThirdRow(
	data: PortfolioListItemResponseDTO,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	const summary = summaryData?.find((item) => item.id === data.id)?.summary;
	const kind = getPortfolioRowValueKind(data.domain, summary != null);

	if (kind === PortfolioRowValueKind.Service) {
		return renderServiceRow(data, theme);
	}

	if (kind === PortfolioRowValueKind.InsuranceDates) {
		return renderInsuranceRow(data, t, theme);
	}

	if (kind === PortfolioRowValueKind.None) {
		return null;
	}

	const rawValue = summary?.totalValue ?? null;
	const currentValue = rawValue == null ? null : roundNumber(rawValue, 0);
	const currencySign = summary
		? getPortfolioCurrencySign(t, summary.currency)
		: t(Resources.Common.CurrencySign);

	if (kind === PortfolioRowValueKind.ValueOnly) {
		return renderValueOnly(currentValue, currencySign);
	}

	const change = normalizeChange(summary?.totalEvaluationPercent);
	return renderValueWithDelta(currentValue, change, currencySign);
}

export function getPortfolioTitle(
	data: {
		name?: string | null;
		product?: string | null;
		domain?: ClientPortfolioDomainEnum | null;
	},
	t: TranslateFunction,
): string {
	const title = data.name ?? data.product;
	if (title) {
		return title;
	}
	return data.domain != null
		? t(Resources.Portfolio.List.PortfolioDomainType[data.domain])
		: "";
}

export function getTitle(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
): string {
	return getPortfolioTitle(data, t);
}
